import { filterConfiguration } from 'app/components/filters/filters-panel/filter-configuration';
import { endpoint } from 'app/area/filters/constants';
import {
  FilterTypes, ConditionValuesOperator, OptionTypes, Filter
} from 'app/components/filters/types';
import { customFilterFieldPostfix } from 'app/components/filters/filters-panel/constants';
import { FilterDateRange } from 'app/components/filters/filters-panel/filter-types/filter-date-range/filter-date-range';
import { getValue } from 'app/components/filters/filters-panel/filter-types/get-value';
import { createDateFieldCondition } from './create-date-condition';
import { createFavouriteCondition } from 'app/components/filters/filters-panel/conditions/create-conditions';
import { FavouriteCondition } from 'app/types-shared';
import { SelectItem } from 'ui/common/select/types';
import { getConditionForOperator } from '../get-condition';

export interface EditingValues {
  [name: string]: {
    value: SelectItem | Date;
  };
}

export const dateFieldConfiguration = {
  [FilterTypes.DateField]: filterConfiguration(
    FilterDateRange,
    () => endpoint.periods,
    [
      customFilterFieldPostfix.fixedPeriod,
      customFilterFieldPostfix.dateIntervalFrom,
      customFilterFieldPostfix.dateIntervalTo
    ]
  )
};

export const dateFieldConditionMapper = (editingValues: EditingValues) => {
  return {
    [FilterTypes.DateField]: (filter: Filter) => {

      const { optionType, advancedField } = filter;

      if (!advancedField) {
        return [];
      }

      if (optionType === OptionTypes.NotSpecified) {

        const originalCondition = getConditionForOperator(filter.conditions, advancedField.valueOperator3);

        return [
          createFavouriteCondition(filter, [{
            id: 1,
            myValue: false,
            periodPredef: { v: 0 },
            operator: advancedField.valueOperator3,
            dbFunction: advancedField.dbFunctionValue3
          }], originalCondition)
        ];
      }

      if (optionType === OptionTypes.SpecificPeriodRange) {

        const originalRangeCondition = getConditionForOperator(filter.conditions, advancedField.valueOperator);
        const valueFrom = getValue(filter, editingValues, customFilterFieldPostfix.dateIntervalFrom);
        const valueTo = getValue(filter, editingValues, customFilterFieldPostfix.dateIntervalTo);
        const hasValueForOneDateField = !!valueFrom || !!valueTo;
        const valueOperatorIfHasValueForOneDateField = hasValueForOneDateField ? 
          (!!valueFrom ? advancedField.valueOperator : advancedField.valueOperator2) : undefined;
        const dbFunctionValueIfHasValueForOneDateField = hasValueForOneDateField ?
          (!!valueTo ? advancedField.dbFunctionValue2 : advancedField.dbFunctionValue) : undefined;
        
        const condition = !!valueFrom && !!valueTo ? 
          createFavouriteCondition(filter, [{
            id: 1,
            ...createDateFieldCondition(valueFrom),
            myValue: false,
            periodPredef: { v: 0 },
            operator: advancedField.valueOperator,
            dbFunction: advancedField.dbFunctionValue
          },
          {
            id: valueFrom ? 2 : 1,
            ...createDateFieldCondition(valueTo),
            myValue: false,
            periodPredef: { v: 0 },
            operator: advancedField.valueOperator2,
            dbFunction: advancedField.dbFunctionValue2
          }], originalRangeCondition)
          : hasValueForOneDateField ? 
              createFavouriteCondition(filter, [{
              id: 1,
              ...createDateFieldCondition(valueFrom || valueTo),
              myValue: false,
              periodPredef: { v: 0 },
              operator: valueOperatorIfHasValueForOneDateField,
              dbFunction: dbFunctionValueIfHasValueForOneDateField
            }], originalRangeCondition) : undefined;

        return [
          ...(!!condition ? [condition] : [])
        ];
      }

      const fixedPeriod = getValue(filter, editingValues, customFilterFieldPostfix.fixedPeriod);
      const fixedPeriodValue = fixedPeriod && {
        periodPredef: {
          v: fixedPeriod.value
        }
      };

      const originalFixedCondition = getConditionForOperator(filter.conditions, { v: 0 });

      return fixedPeriodValue ? [
        createFavouriteCondition(filter, [{
          id: 1,
          ...fixedPeriodValue,
          myValue: false,
          operator: { v: 0 },
          dbFunction: { v: 0 }
        }], originalFixedCondition)
      ] : [];
    }
  };
};

export const dateFieldOptionType = (condition: FavouriteCondition) => {

  if (condition.values && condition.values.some(v => v.operator && v.operator.v === ConditionValuesOperator.empty)) {
    return {
      [FilterTypes.DateField]: OptionTypes.NotSpecified
    };
  }

  if (condition.values
    && condition.values.some(
      v => v.operator
        && (v.operator.v === ConditionValuesOperator.largerOrEqual || v.operator.v === ConditionValuesOperator.less)
    )) {
    return {
      [FilterTypes.DateField]: OptionTypes.SpecificPeriodRange
    };
  }

  return {
    [FilterTypes.DateField]: OptionTypes.FixedPeriod
  };
};
